import * as React from 'react';
import { CalendarIcon } from 'lucide-react';
import { Button } from './button.js';
import { POPOVER_SIDE_OFFSET, PopoverContent, PopoverTrigger } from './popover.js';

export interface DatePickerTriggerProps {
  /** Trigger label content: formatted date, range label, or a placeholder span. */
  children: React.ReactNode;
  disabled?: boolean;
  /** Forwarded to the trigger Button so a `<Label htmlFor>` can target it. */
  id?: string;
  /** Forwarded to the trigger Button for screen-reader field naming. */
  'aria-label'?: string;
  testId?: string;
  /**
   * Current "from" value, exposed as `data-from-value` so tests can assert a
   * selected range without parsing the locale-formatted trigger label.
   */
  'data-from-value'?: string;
  /** Current "to" value, exposed as `data-to-value` — see `data-from-value`. */
  'data-to-value'?: string;
}

/**
 * Shared `PopoverTrigger` + `Button` + `CalendarIcon` composition used by
 * both `DatePickerButton` and `DateRangeButtonField`, so the trigger markup
 * and classes don't drift between the two. The forwarded ref reaches the
 * trigger `<button>` so `useCalendarPopoverSide` can measure it.
 */
export const DatePickerTrigger = React.forwardRef<HTMLButtonElement, DatePickerTriggerProps>(
  function DatePickerTrigger(
    {
      children,
      disabled,
      id,
      'aria-label': ariaLabel,
      testId,
      'data-from-value': dataFromValue,
      'data-to-value': dataToValue,
    },
    ref,
  ) {
    return (
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          id={id}
          aria-label={ariaLabel}
          className="w-full justify-start font-normal"
          data-testid={testId}
          data-from-value={dataFromValue}
          data-to-value={dataToValue}
        >
          <CalendarIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          {children}
        </Button>
      </PopoverTrigger>
    );
  },
);

export interface DatePickerPopoverContentProps {
  children: React.ReactNode;
  /** Placement computed by `useCalendarPopoverSide`. Defaults to 'bottom'. */
  side?: CalendarPopoverSide;
  /** Horizontal alignment computed by `useCalendarPopoverSide`. Defaults to 'start'. */
  align?: CalendarPopoverAlign;
}

const NARROW_CALENDAR_MEDIA_QUERY = '(max-width: 639px)';

export function useNarrowCalendarViewport() {
  const [isNarrow, setIsNarrow] = React.useState(false);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia?.(NARROW_CALENDAR_MEDIA_QUERY);
    if (!mediaQuery) return;
    const update = () => setIsNarrow(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return isNarrow;
}

export type CalendarPopoverSide = 'top' | 'bottom';
export type CalendarPopoverAlign = 'start' | 'end';

/**
 * Decides once per open whether the calendar popover fits below its trigger
 * or must open above it (CU-86cbe4vuq: a field low on the page pushed the
 * calendar's bottom half off-screen, making dates unclickable), AND whether
 * it fits to the right of the trigger (`align="start"`, the default) or must
 * hug the trigger's right edge instead (`align="end"`) (CU-86cbh30z4: inside
 * the offer edit drawer — which docks flush against the viewport's right
 * edge — the "Czas trwania kampanii" range field sits in the drawer's
 * trailing grid column, so its two-month calendar opened past the right edge
 * of the browser window and was invisible without scrolling).
 *
 * Radix's own `avoidCollisions` is deliberately NOT used for either axis on
 * regular viewports: it re-evaluates continuously, so a calendar whose size
 * changes between months flip-flopped top/bottom mid-navigation
 * (CU-86capuj0d, the "jumping months" bug) — the same would happen on the
 * horizontal axis. Instead, `contentRef` is a callback ref that measures the
 * trigger and the content exactly once when the content mounts (i.e. once
 * per open, before paint — Radix's Portal mounts one sync flush after the
 * open re-render, so a layout effect keyed on `open` would run too early and
 * see no content). Both `side` and `align` stay pinned for the whole time
 * the popover is open.
 *
 * Known limitation (accepted): neither axis is re-measured when the
 * viewport resizes while the popover is open (rotation, on-screen keyboard,
 * devtools pane). Closing and reopening the picker recovers; a resize
 * listener was judged not worth the extra mid-open repositioning it would
 * reintroduce.
 *
 * Wire `triggerRef` to `DatePickerTrigger`, `contentRef` + `side` + `align`
 * to `DatePickerPopoverContent`.
 */
export function useCalendarPopoverSide() {
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const [side, setSide] = React.useState<CalendarPopoverSide>('bottom');
  const [align, setAlign] = React.useState<CalendarPopoverAlign>('start');

  const contentRef = React.useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    const triggerRect = triggerRef.current?.getBoundingClientRect();
    // offsetHeight/offsetWidth ignore the popover's zoom-in entry transform,
    // unlike getBoundingClientRect().
    const contentHeight = node.offsetHeight;
    const contentWidth = node.offsetWidth;
    if (!triggerRect || contentHeight === 0) return;
    const spaceBelow = window.innerHeight - triggerRect.bottom - POPOVER_SIDE_OFFSET;
    const spaceAbove = triggerRect.top - POPOVER_SIDE_OFFSET;
    // Open above only when the calendar doesn't fit below AND there is more
    // room above — otherwise 'bottom' remains the better (or equal) side.
    setSide(contentHeight > spaceBelow && spaceAbove > spaceBelow ? 'top' : 'bottom');

    const spaceRight = window.innerWidth - triggerRect.left;
    const spaceLeft = triggerRect.right;
    // Hug the trigger's right edge only when 'start' doesn't fit AND there is
    // more room to the left — otherwise 'start' remains the better (or equal)
    // alignment.
    setAlign(contentWidth > spaceRight && spaceLeft > spaceRight ? 'end' : 'start');
  }, []);

  return { triggerRef, contentRef, side, align };
}

/**
 * Shared `PopoverContent` wrapper (className/align/position props) for
 * calendar popovers, used by both `DatePickerButton` and
 * `DateRangeButtonField`. The forwarded ref reaches the content element so
 * `useCalendarPopoverSide` can measure its height. Narrow viewports keep
 * Radix's collision-aware placement (single-month calendar, tight space).
 */
export const DatePickerPopoverContent = React.forwardRef<
  HTMLDivElement,
  DatePickerPopoverContentProps
>(function DatePickerPopoverContent({ children, side = 'bottom', align = 'start' }, ref) {
  const isNarrow = useNarrowCalendarViewport();

  return (
    <PopoverContent
      ref={ref}
      className="w-auto p-0"
      align={align}
      side={side}
      avoidCollisions={isNarrow}
    >
      {children}
    </PopoverContent>
  );
});
